import React, {useState, Fragment, useEffect, useRef} from 'react';
import {Modal, Button, Input, Select, Space, Table, Typography, Popconfirm} from 'antd';
import {set, remove} from 'lodash';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';

// antd 6 deprecated the `index` parameter on rowKey callbacks, and two
// freshly-added rows with identical shape otherwise collide on the
// content-hashed default key. Each row gets a monotonic rowId assigned
// at creation time; it's stable across edits and used only for React
// reconciliation — it's stripped before the fields are handed to the
// parent via onAddField.
function makeRow(seedId, overrides = {}) {
    return {__rowId: seedId, name: '', type: 'string', ...overrides};
}

export function AddSchemaFieldBulkDialog({onAddField, title}) {
    const [visible, setVisible] = useState();
    const rowIdSeed = useRef(0);
    const nextRowId = () => ++rowIdSeed.current;
    const [fields, setFields] = useState(() => [makeRow(nextRowId())]);
    const [isValid, setIsValid] = useState();

    useEffect(() => {
        setIsValid(
            fields.reduce((valid, f) => {
                return valid && f.name.length > 0 && !!f.name.match(/^[\D]{1}[\w]+/);
            }, true)
        );
    }, [fields]);

    async function handleAdd() {
        // Strip the internal __rowId before handing off — downstream code
        // stores the field objects directly on the schema.
        const cleaned = fields.map(({__rowId, ...rest}) => rest);
        onAddField && onAddField(cleaned);
        setVisible(false);
        setFields([makeRow(nextRowId())]);
        setIsValid(false);
    }

    function handleCancel() {
        setVisible(false);
        setFields([makeRow(nextRowId())]);
    }

    const addFieldRow = () => {
        setFields([...fields, makeRow(nextRowId())]);
    };

    function renderTableFooter() {
        return (
            <Space>
                <Button size="small" className="addButton" type="primary" icon={<PlusOutlined />} onClick={addFieldRow}>
                    Add Field
                </Button>
            </Space>
        );
    }

    function handleFieldChange(fieldName, field, value) {
        set(field, fieldName, value);
        setFields([...fields]);
    }

    function handleDeleteField(target) {
        return function () {
            // Remove by the stable rowId rather than the user-entered name:
            // while typing, multiple rows can transiently share an empty or
            // duplicate name, and a name-based remove would take them all.
            remove(fields, (f) => f.__rowId === target.__rowId);
            setFields([...fields]);
        };
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            render: (name, record) => <Input value={name} onChange={(e) => handleFieldChange('name', record, e.target.value)} />,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            render: (type, record) => (
                <Select value={type} style={{width: '100%'}} onChange={(value) => handleFieldChange('type', record, value)}>
                    <Select.Option value="string">string</Select.Option>
                    <Select.Option value="number">number</Select.Option>
                    <Select.Option value="boolean">boolean</Select.Option>
                    <Select.Option value="object">object</Select.Option>
                    <Select.Option value="array">array</Select.Option>
                </Select>
            ),
        },
        {
            title: 'Format',
            dataIndex: 'format',
            render: (format, record) => (
                <Select style={{width: '100%'}} value={format} onChange={(value) => handleFieldChange('format', record, value)}>
                    <Select.Option value="">(unspecified)</Select.Option>
                    {record.type === 'number' && <Select.Option value="int32">integer</Select.Option>}
                    {record.type === 'number' && <Select.Option value="int64">long</Select.Option>}
                    {record.type === 'number' && <Select.Option value="float">float</Select.Option>}
                    {record.type === 'number' && <Select.Option value="double">double</Select.Option>}
                    {record.type === 'string' && <Select.Option value="base64">base64</Select.Option>}
                    {record.type === 'string' && <Select.Option value="binary">binary</Select.Option>}
                    {record.type === 'string' && <Select.Option value="date">date</Select.Option>}
                    {record.type === 'string' && <Select.Option value="date-time">date-time</Select.Option>}
                    {record.type === 'string' && <Select.Option value="password">password</Select.Option>}
                </Select>
            ),
        },
        {
            render: (record) => (
                <Popconfirm okType="default" title={`Delete field ${record.name}?`} onConfirm={handleDeleteField(record)}>
                    <Button className="deleteIcon" type="link" danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Fragment>
            <Button className="addField" size="small" type="primary" onClick={() => setVisible(true)}>
                {title || 'Add Fields...'}
            </Button>
            {visible && (
                <Modal
                    title={title || 'Add Fields...'}
                    open={visible}
                    width={700}
                    destroyOnHidden
                    footer={[
                        <Button className="addRef" key="back" onClick={handleCancel}>
                            Cancel
                        </Button>,
                        <Button className="addField" key="submit" type="primary" disabled={!isValid} onClick={handleAdd}>
                            Confirm
                        </Button>,
                    ]}
                    onCancel={() => setVisible(false)}>
                    <Typography.Paragraph> Add multiple fields to the current schema. You can always edit these fields once they are created. </Typography.Paragraph>
                    <Table dataSource={fields} columns={columns} pagination={false} footer={renderTableFooter} rowKey="__rowId" />
                </Modal>
            )}
        </Fragment>
    );
}
